import * as THREE from "three";
import type { Rng } from "../rng";
import { flat, Tone } from "../materials";
import { Slot, slotNormal } from "../palette";
import { blobGeometry } from "../geometry";
import { ITEMS } from "../items";
import type { ItemId } from "../items";

/** what the chest holds — the app puts these in the inventory when it opens */
/** what the cache by the rotunda holds */
export const CHEST_LOOT: readonly ItemId[] = ["glasses", "clock"];

const GLOW = new THREE.MeshBasicMaterial({ color: 0xffffff });

const asFire = (mesh: THREE.Mesh, variant: number): THREE.Mesh => {
  mesh.layers.enable(1);
  mesh.userData.slot = slotNormal(Slot.fire);
  mesh.userData.variant = variant;
  return mesh;
};

const tag = (mesh: THREE.Mesh, slot: number, variant: number): THREE.Mesh => {
  mesh.userData.slot = slotNormal(slot);
  mesh.userData.variant = variant;
  return mesh;
};

const solid = (mesh: THREE.Mesh, slot: number, variant: number): THREE.Mesh => {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return tag(mesh, slot, variant);
};

export interface ChestItem {
  id: ItemId;
  root: THREE.Group;
  rest: THREE.Vector3;
  yaw: number;
  taken: boolean;
  lift: number;
}

export interface Chest {
  group: THREE.Group;
  readonly items: ChestItem[];
  opened: boolean;
  open(): void;
  close(): void;
  take(item: ChestItem): void;
  restore(item: ChestItem): void;
  update(delta: number, time: number, daylight: number): void;
}

function glowTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (context) {
    const gradient = context.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    );
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.3, "rgba(255,255,255,0.5)");
    gradient.addColorStop(0.65, "rgba(255,255,255,0.12)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.LinearSRGBColorSpace;
  return texture;
}

const LID_OPEN = 2.52;
// the bloom is light, not geometry: an emissive cone renders as a flat orange
// lump in a duotone, where a spill of light reads immediately
const LIGHT_DAY = 52;
const LIGHT_NIGHT = 130;

export function buildChest(rng: Rng): Chest {
  const group = new THREE.Group();

  const width = 1.04;
  const height = 0.5;
  const depth = 0.68;
  const radius = depth / 2;

  // pale sun-bleached wood against dark iron: the chest has to read on a bright
  // plaza and on a black night both, so the value range matters more than the hue
  const wood = flat(Tone.ash);
  const iron = flat(Tone.ink);
  const variant = rng.range(0.52, 0.68);

  const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), wood);
  body.position.y = height / 2;
  group.add(solid(body, Slot.wood, variant));

  for (const side of [-1, 1]) {
    const strap = new THREE.Mesh(new THREE.BoxGeometry(0.08, height + 0.03, depth + 0.03), iron);
    strap.position.set(side * 0.3, height / 2, 0);
    group.add(solid(strap, Slot.metal, 0.42));
  }

  for (const [x, z] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ] as const) {
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.05, 0.1), iron);
    foot.position.set(x * (width / 2 - 0.07), 0.025, z * (depth / 2 - 0.07));
    group.add(tag(foot, Slot.metal, 0.3));
  }

  // the lid hinges on its back edge, so it needs to live in its own pivot
  const lid = new THREE.Group();
  lid.position.set(0, height, -radius);
  group.add(lid);

  const plate = new THREE.Mesh(new THREE.BoxGeometry(width, 0.05, depth), wood);
  plate.position.set(0, 0.025, radius);
  lid.add(solid(plate, Slot.wood, variant));


  const barrelGeometry = new THREE.CylinderGeometry(radius, radius, width, 16, 1, false, 0, Math.PI);
  barrelGeometry.rotateZ(Math.PI / 2);
  const barrel = new THREE.Mesh(barrelGeometry, wood);
  barrel.position.set(0, 0.05, radius);
  lid.add(solid(barrel, Slot.wood, variant + 0.06));

  // the body's straps carry on up and over the barrel as a band that follows the
  // arch, rather than stopping dead at the rim
  for (const side of [-1, 1]) {
    const bandGeometry = new THREE.CylinderGeometry(
      radius + 0.015,
      radius + 0.015,
      0.085,
      16,
      1,
      true,
      0,
      Math.PI,
    );
    bandGeometry.rotateZ(Math.PI / 2);
    const band = new THREE.Mesh(bandGeometry, iron);
    band.position.set(side * 0.3, 0.05, radius);
    lid.add(solid(band, Slot.metal, 0.42));
  }


  const clasp = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.17, 0.035), iron);
  clasp.position.set(0, height - 0.07, depth / 2 + 0.015);
  clasp.castShadow = true;
  group.add(tag(clasp, Slot.metal, 0.5));

  // what is inside starts hidden, and stays there until you take it out by hand
  const items: ChestItem[] = CHEST_LOOT.filter((id) => ITEMS[id].build).map((id, index) => {
    const root = new THREE.Group();
    const model = ITEMS[id].build!();
    model.scale.setScalar(0.78);
    root.add(model);
    const rest = new THREE.Vector3(index === 0 ? -0.2 : 0.19, 0.14, index === 0 ? 0.05 : -0.05);
    const yaw = index === 0 ? -0.5 : 0.4;
    root.position.copy(rest);
    root.rotation.y = yaw;
    root.visible = false;
    group.add(root);
    return { id, root, rest, yaw, taken: false, lift: 0 };
  });

  // everything glows from the floor of the box, so the light climbs out of it
  // rather than sitting on top like a lid ornament
  const coreGeometry = blobGeometry(0.13, 1, 0.4, rng);
  coreGeometry.scale(1.5, 0.55, 1.1);
  const core = asFire(new THREE.Mesh(coreGeometry, GLOW), 0.06);
  core.position.set(0, 0.1, 0);
  core.visible = false;
  group.add(core);

  const light = new THREE.PointLight(0xffb066, 0, 15, 1.2);
  light.position.set(0, 0.16, 0);
  group.add(light);

  // the bloom itself: an additive sprite, so it stays a soft halo through the
  // duotone instead of the flat ember lump any accent geometry becomes
  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowTexture(),
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      color: 0xffcf9c,
      opacity: 0,
    }),
  );
  halo.position.set(0, 0.3, 0);
  halo.scale.setScalar(1.1);
  group.add(halo);

  // a pool on the ground, so the spill reaches the flagstones around the base
  const pool = new THREE.Mesh(
    new THREE.CircleGeometry(1.75, 30),
    new THREE.MeshBasicMaterial({
      map: glowTexture(),
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      color: 0xffb877,
      opacity: 0,
    }),
  );
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.035;
  group.add(pool);

  let opened = false;
  let lidAmount = 0;
  let glow = 0;

  const update = (delta: number, time: number, daylight: number): void => {
    lidAmount += ((opened ? 1 : 0) - lidAmount) * (1 - Math.exp(-delta / 0.26));
    if (Math.abs((opened ? 1 : 0) - lidAmount) < 0.002) lidAmount = opened ? 1 : 0;
    lid.rotation.x = -lidAmount * LID_OPEN;

    for (const item of items) {
      if (item.taken && item.lift < 1) {
        if (!opened) {
          item.lift = 1;
        } else {
          item.lift = Math.min(1, item.lift + delta / 0.5);
          item.root.position.y = item.rest.y + item.lift * 0.62;
          item.root.rotation.y += delta * 2.4;
          item.root.scale.setScalar(1 - item.lift * 0.55);
        }
      }
      item.root.visible = opened && !(item.taken && item.lift >= 1);
    }

    const night = 1 - Math.min(1, Math.max(0, daylight));
    // the light holds while there is still something in the box, then sinks
    const remaining = items.some((item) => !item.taken);
    const target = opened && remaining ? lidAmount : 0;
    glow += (target - glow) * (1 - Math.exp(-delta / (target > glow ? 0.3 : 0.55)));

    const base = LIGHT_DAY + (LIGHT_NIGHT - LIGHT_DAY) * night;
    // slow and shallow: a fast flicker on the underside of the lid is distracting
    const flicker = 0.98 + Math.sin(time * 1.6) * 0.02 + Math.sin(time * 3.9) * 0.01;
    // a closed chest keeps a dim ember, so a cache reads as a cache from across the plaza
    const idle = (1 - lidAmount) * 0.16;
    const lit = glow + idle;
    light.intensity = base * lit * flicker;
    halo.material.opacity = Math.min(1, lit * 0.95) * flicker * (0.55 + 0.45 * night);
    halo.scale.setScalar(0.8 + lit * 1.1);
    pool.material.opacity = Math.min(1, lit) * flicker * (0.3 + 0.7 * night);
    pool.scale.setScalar(0.8 + lit * 0.6);
    core.visible = lit > 0.02;
    core.scale.setScalar(0.72 + lit * 0.5 + Math.sin(time * 3.1) * 0.04);
  };

  update(0, 0, 1);

  return {
    group,
    items,
    get opened(): boolean {
      return opened;
    },
    take(item: ChestItem): void {
      item.taken = true;
    },
    restore(item: ChestItem): void {
      item.taken = false;
      item.lift = 0;
      item.root.position.copy(item.rest);
      item.root.rotation.set(0, item.yaw, 0);
      item.root.scale.setScalar(1);
      item.root.visible = opened;
    },
    open(): void {
      if (opened) return;
      opened = true;
    },
    close(): void {
      if (!opened) return;
      opened = false;
    },
    update,
  };
}
